import {
  Flag,
  countryName,
  formatRange,
  formatWhole,
  daysHeld,
  heldBy,
  partedBy,
  type Money,
  type Plan,
  type PlanStop,
  type Variant,
} from '@trippath/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { dayRange } from '../../lib/countryFacts';
import { gapsOf, type Gap } from '../../lib/gaps';
import { pairs } from '../../lib/map';
import { read, write } from '../../components/widget/Widget';
import { runs } from './runs';
import { ChangeMark, ModeIcon } from '../../components/icons/ModeIcon';
import { StatusIcon } from '../../components/icons/StatusIcon';
import { variantName } from '../../components/variants/Variants';
import { useReadOnly } from '../../app/readOnly';

/** One row of the route: a country, a stop, or a place of a variant in its lane. */
type Item =
  | {
      kind: 'run';
      key: string;
      country: string;
      days: number;
      dear: boolean;
      stops: string[];
    }
  | { kind: 'stop'; key: string; stop: PlanStop }
  | { kind: 'gap'; key: string; gap: Gap }
  | { kind: 'place'; key: string; variant: Variant; lane: number; stop: PlanStop; at: number };

/** How tall each row is, and how far apart the lanes are, in pixels. */
const ROW = 40;
/** How many rows days no stop covers take: their words, their dates, what to do; one more for
 * the ways on when how it goes is not decided. */
const GAP_ROWS = 3;
/** A country's head is lower than a stop's row: its words alone, no room wasted round them. */
const RUN_ROWS = 0.7;
/** Where the browser keeps whether the route says totals. */
const SUMS = 'trippath-route-sums';
const LANE = 14;

/** No stop said more of: one list, not a new one each time it is drawn. */
export const NO_NOTES: Readonly<Record<string, string>> = {};

interface Props {
  plan: Plan;
  variants: readonly Variant[];
  /** The stop picked, shown on the map. */
  selected: string | null;
  /** The variants shown on the map. */
  picked: readonly string[];
  /** A stop pressed: picked, its details shown. */
  onSelect: (stop: string) => void;
  /** A variant pressed: shown, or put away. */
  onPick: (variant: string) => void;
  /** The plan's own line pressed: back to the plan as it is. */
  onClear: () => void;
  /** Moves the stop at `from` to `to`, by its place in the route. */
  onMove: (from: number, to: number) => void;
  /** A stop's days typed in its row. */
  onDays: (stop: PlanStop, days: number) => void;
  /** A variant's new place's days typed in its row, by its place among them. */
  onPlaceDays: (variant: Variant, at: number, stop: PlanStop, days: number) => void;
  /** A stop asked for in days no stop covers, after the stop they follow. */
  onAddAfter: (stop: PlanStop) => void;
  /** Said under the stops: the other trips of its family. */
  after?: ReactNode;
  /** Said after a stop's days: `3 trips from here` where its family parts. */
  notes?: Readonly<Record<string, string>>;
  /** Its colour in its family, `kin-2`: its rail drawn in it. */
  kin?: string;
}

/** A stop's or a place's days being typed, and where they are kept. */
interface Typing {
  stop: PlanStop;
  text: string;
  save: (days: number) => void;
}

/**
 * The variants that fit the plan, each with its lane beside the plan's — the nearest one free
 * along its stretch, so branches far apart share it: one that no longer fits has no branch.
 */
function lanesOf(
  plan: Plan,
  variants: readonly Variant[],
): { variant: Variant; lane: number }[] {
  // Where each goes along the plan, by its stops: one from elsewhere after the end.
  const at = (id: string | undefined, none: number) => {
    const found = plan.stops.findIndex((stop) => stop.id === id);
    return found === -1 ? none : found;
  };
  const placed: { variant: Variant; lane: number; start: number; end: number }[] = [];
  for (const variant of variants.filter((v) => v.delta !== null)) {
    const start = at(variant.from?.id, plan.stops.length);
    const end = at(variant.rejoin?.id, plan.stops.length + 1);
    // The nearest lane no variant over the same stretch holds.
    const taken = new Set(
      placed.filter((p) => p.start < end && start < p.end).map((p) => p.lane),
    );
    let lane = 1;
    while (taken.has(lane)) lane += 1;
    placed.push({ variant, lane, start, end });
  }
  return placed.map(({ variant, lane }) => ({ variant, lane }));
}

/** How a variant's new place is picked and opened: by its variant and its place in it. */
export function placeKey(variant: Variant, at: number): string {
  return `p-${variant.id}-${String(at)}`;
}

/** The variant's new place picked by its key, if one is. */
export function placeOf(
  variants: readonly Variant[],
  key: string | null,
): { variant: Variant; stop: PlanStop; at: number } | null {
  for (const variant of variants) {
    const at = variant.added.findIndex((_, i) => placeKey(variant, i) === key);
    const stop = variant.added[at];
    if (stop) return { variant, stop, at };
  }
  return null;
}

/** The rows of a route with its variants: each one's places under the stop it leaves from. */
export function routeItems(plan: Plan, variants: readonly Variant[]): Item[] {
  const fits = lanesOf(plan, variants);
  const gaps = gapsOf(plan);
  const placesOf = ({ variant, lane }: (typeof fits)[number]): Item[] =>
    variant.added.map((stop, at) => ({
      kind: 'place',
      key: placeKey(variant, at),
      variant,
      lane,
      stop,
      at,
    }));
  return [
    ...runs(plan.stops).flatMap((run, r): Item[] => [
      {
        kind: 'run',
        key: `r-${String(r)}`,
        country: run.country,
        days: run.days,
        dear: run.stops.some((placed) => placed.stop.status === 'expensive'),
        stops: run.stops.map((placed) => placed.stop.id),
      },
      ...run.stops.flatMap(({ stop }): Item[] => {
        const gap = gaps.get(stop.id);
        const unplanned: Item[] = gap ? [{ kind: 'gap', key: `g-${stop.id}`, gap }] : [];
        return [
          { kind: 'stop', key: stop.id, stop },
          ...fits.filter((f) => f.variant.from?.id === stop.id).flatMap(placesOf),
          ...unplanned,
        ];
      }),
    ]),
    ...fits.filter((f) => f.variant.from === null).flatMap(placesOf),
  ];
}

/**
 * The route as a tree of branches: the plan's stops down the first lane, by country, and each
 * variant in a lane of its own — out of the stop it leaves from, along its places, back into the
 * plan where it joins. A stop is picked to see it on the map, its dot ringed, and opened by its ›
 * for its details; stops are dragged into their order. A variant is picked by its line or its
 * places: it stands out, the others fade, and the stops it skips are struck.
 */
export function RouteList({
  plan,
  variants,
  selected,
  picked,
  onSelect,
  onPick,
  onClear,
  onMove,
  onDays,
  onPlaceDays,
  onAddAfter,
  after,
  notes = NO_NOTES,
  kin,
}: Props) {
  const { t, i18n } = useTranslation();
  // A trip read only: its route looked at, nothing moved, typed or added.
  const locked = useReadOnly();
  const lang = i18n.language;
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // A stop's days being typed in its row: opened by a double click or F2, kept on Enter or on
  // leaving the field, put away by Escape.
  const [typing, setTyping] = useState<Typing | null>(null);
  // Each row's day, or what it costs in all: as this browser last had it.
  const [sums, setSums] = useState(() => read(SUMS) === true);
  const showSums = (on: boolean) => {
    setSums(on);
    write(SUMS, on);
  };
  const startTyping = (stop: PlanStop, save: (days: number) => void) => {
    if (locked) return;
    setTyping({ stop, text: String(stop.days), save });
  };
  // Once put away the field is gone, and React tells it of no blur after that.
  const finish = (now: Typing, keep: boolean) => {
    const days = /^\d{1,3}$/.test(now.text.trim()) ? Number(now.text) : null;
    if (keep && days !== null && days !== now.stop.days) now.save(days);
    setTyping(null);
  };
  // The days typed where they were shown: the name, the field, then what they are.
  // Its days typed in their place: the row as it was, its number a field.
  const daysForm = (now: Typing, className: string, tail: ReactNode) => (
    <form
      className={`${className} stop-typing`}
      onSubmit={(event) => {
        event.preventDefault();
        finish(now, true);
      }}
    >
      <span className="stop-name">
        {now.stop.city} ·{' '}
        <input
          ref={(field) => {
            field?.focus();
          }}
          className="days-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={t('plan.editDays', { city: now.stop.city })}
          value={now.text}
          onChange={(event) => {
            setTyping({ ...now, text: event.target.value });
          }}
          onBlur={() => {
            finish(now, true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') finish(now, false);
          }}
        />
        {t('plan.daysUnit')}
      </span>
      {tail}
    </form>
  );
  const items = routeItems(plan, variants);
  const fits = lanesOf(plan, variants);
  const gutter = 10 + (Math.max(0, ...fits.map((f) => f.lane)) + 1) * LANE;
  const skipped = new Set(
    variants.filter((v) => picked.includes(v.id)).flatMap((v) => v.dropped.map((d) => d.id)),
  );
  const perDay = new Map(plan.countries.map((c) => [c.country, c.expectedPerDay]));
  // A day's cost, or what it costs in all, as the route is set to say.
  const amount = (day: Money, total: Money) =>
    sums
      ? formatWhole(total, lang)
      : t('plan.perDayShortAmount', { amount: formatWhole(day, lang) });
  // A country's days and a day's cost with the variants shown: what each changes there, added
  // up — they change different stretches. A country the route reaches twice changes at its first.
  const shownNow = variants.filter((v) => picked.includes(v.id));
  const figures = (run: Extract<Item, { kind: 'run' }>) => {
    const planned = plan.countries.find((c) => c.country === run.country);
    const first = items.find((item) => item.kind === 'run' && item.country === run.country);
    // What the stretch costs in all: its stops', or the country's as the variants change it.
    const sum = plan.stops
      .filter((stop) => run.stops.includes(stop.id))
      .reduce((all, stop) => all + stop.cost.expected.minor, 0);
    const same = {
      days: run.days,
      // A stretch of no days, the airport the trip ends at, has no day to cost.
      day: run.days === 0 ? null : (perDay.get(run.country) ?? null),
      total: { currency: plan.trip.currency, minor: sum },
      changed: false,
    };
    if (!planned || first !== run) return same;
    let more = 0;
    let expected = planned.expected.minor;
    for (const variant of shownNow) {
      const after = variant.countries.find((c) => c.country === run.country);
      more += (after ? after.days : 0) - planned.days;
      expected += (after ? after.expected.minor : 0) - planned.expected.minor;
    }
    if (more === 0 && expected === planned.expected.minor) return same;
    // A day's cost is the country's: what it all costs over all its days.
    const all = planned.days + more;
    const day: Money | null =
      all > 0 ? { currency: plan.trip.currency, minor: Math.round(expected / all) } : null;
    return {
      days: run.days + more,
      day,
      total: { currency: plan.trip.currency, minor: expected },
      changed: true,
    };
  };
  const rowOf = (key: string) => items.findIndex((item) => item.key === key);
  // The plan's line far enough in that the stop picked, drawn larger, is whole.
  const x = (lane: number) => 12 + lane * LANE;
  // Each row's height in rows: days no stop covers take more, the rest one.
  const waysOf = (gap: Gap) => (gap.after.out === null ? gap.after.ways : []);
  const spans = items.map((item) =>
    item.kind === 'gap'
      ? GAP_ROWS + Math.min(1, waysOf(item.gap).length)
      : item.kind === 'run'
        ? RUN_ROWS
        : 1,
  );
  const y = (row: number) =>
    spans.slice(0, row).reduce((sum, span) => sum + span, 0) * ROW + ROW / 2;
  const ids = plan.stops.map((stop) => stop.id);
  // A stop's days typed in its row, unless the tickets there and on hold them.
  // A change on the way has no days to type: it is passed through.
  const type = (stop: PlanStop) => {
    if (stop.transfer || daysHeld(plan, stop)) return;
    startTyping(stop, (days) => {
      onDays(stop, days);
    });
  };
  // Whether the stop at `from` may go to `to`: no booked way is parted by it.
  const allowed = (from: number, to: number) => {
    if (from < 0) return true;
    const order = ids.toSpliced(from, 1).toSpliced(to, 0, ...ids.slice(from, from + 1));
    return partedBy(plan, order) === null;
  };
  const pick = (variant: Variant) => {
    onPick(variant.id);
  };

  // The plan's line from its first stop to its last, and each variant's out and back.
  const stopRows = items.flatMap((item, at) => (item.kind === 'stop' ? [at] : []));
  // The plan's line, stop to stop: a stretch a variant picked goes round is faded.
  const shown = variants.filter((v) => picked.includes(v.id));
  const round = (a: string, b: string) =>
    skipped.has(a) ||
    skipped.has(b) ||
    shown.some((v) => v.from?.id === a && v.rejoin?.id === b);
  const placed = items.flatMap((item, row) =>
    item.kind === 'stop' ? [{ row, id: item.stop.id }] : [],
  );
  const stretches = pairs(placed).map(([a, b]) => ({
    key: `${a.id}-${b.id}`,
    from: a.row,
    to: b.row,
    faded: round(a.id, b.id),
    // Through days no stop covers, the line is dashed.
    open: items.slice(a.row, b.row).some((item) => item.kind === 'gap'),
  }));

  const [top] = stopRows;
  const bottom = stopRows.at(-1);
  const lines = fits.map(({ variant, lane }) => {
    const at = x(lane);
    const rows = items.flatMap((item, row) =>
      item.kind === 'place' && item.variant.id === variant.id ? [row] : [],
    );
    // Out of the stop it leaves into its lane; one from elsewhere starts at its first place.
    const [first] = rows;
    let d = first === undefined ? '' : `M${String(at)} ${String(y(first))}`;
    if (variant.from !== null) {
      const from = y(rowOf(variant.from.id));
      d = `M${String(x(0))} ${String(from)}C${String(x(0))} ${String(from + ROW / 2)} ${String(at)} ${String(from + ROW / 2)} ${String(at)} ${String(from + ROW)}`;
    }
    const last = rows.at(-1);
    if (last !== undefined) d += `V${String(y(last))}`;
    if (variant.rejoin) {
      const to = y(rowOf(variant.rejoin.id));
      d += `V${String(to - ROW)}C${String(at)} ${String(to - ROW / 2)} ${String(x(0))} ${String(to - ROW / 2)} ${String(x(0))} ${String(to)}`;
    }
    return { variant, d };
  });

  return (
    <section
      className={kin === undefined ? 'panel route-list' : `panel route-list route-kin ${kin}`}
      aria-labelledby="route-title"
    >
      <div className="route-list-head">
        <h2 id="route-title">{t('plan.route')}</h2>
      </div>
      {plan.stops.length === 0 && <p className="quiet route-empty">{t('plan.noStops')}</p>}
      <div className={picked.length === 0 ? 'route-tree' : 'route-tree route-tree-picked'}>
        <svg
          className="route-rail"
          width={gutter}
          height={spans.reduce((sum, span) => sum + span, 0) * ROW}
        >
          {stretches.map((stretch) => (
            <path
              key={stretch.key}
              className={[
                'rail-main',
                stretch.faded ? 'rail-main-faded' : '',
                stretch.open ? 'rail-open' : '',
              ].join(' ')}
              d={`M${String(x(0))} ${String(y(stretch.from))}V${String(y(stretch.to))}`}
            />
          ))}
          {top !== undefined && bottom !== undefined && top !== bottom && (
            // The plan's own line is picked too: back to the plan as it is.
            <path
              className="rail-hit"
              d={`M${String(x(0))} ${String(y(top))}V${String(y(bottom))}`}
              role="button"
              tabIndex={0}
              aria-label={t('plan.showPlan')}
              aria-pressed={picked.length === 0}
              onClick={() => {
                onClear();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onClear();
              }}
            />
          )}
          {lines.map(({ variant, d }) => (
            <g
              key={variant.id}
              className={
                picked.includes(variant.id) ? 'rail-variant rail-picked' : 'rail-variant'
              }
            >
              <path className="rail-branch" d={d} />
              {/* A wide, clear line over it: the branch is picked by its line. */}
              <path
                className="rail-hit"
                d={d}
                role="button"
                tabIndex={0}
                aria-label={t('plan.showVariant', { name: variantName(variant) })}
                aria-pressed={picked.includes(variant.id)}
                onClick={() => {
                  pick(variant);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  pick(variant);
                }}
              />
            </g>
          ))}
          {items.map((item, at) => {
            if (item.kind === 'stop') {
              const classes = ['rail-stop'];
              // A change on the way: a small open ring, not a stop.
              if (item.stop.transfer) classes.push('rail-transfer');
              if (skipped.has(item.stop.id)) classes.push('rail-skipped');
              if (selected === item.stop.id) classes.push('rail-selected');
              return (
                <circle
                  key={item.key}
                  cx={x(0)}
                  cy={y(at)}
                  r={selected === item.stop.id ? 7 : item.stop.transfer ? 3 : 5}
                  className={classes.join(' ')}
                  aria-hidden="true"
                />
              );
            }
            if (item.kind === 'place') {
              const classes = ['rail-place'];
              if (picked.includes(item.variant.id)) classes.push('rail-place-picked');
              if (selected === item.key) classes.push('rail-selected');
              return (
                <circle
                  key={item.key}
                  cx={x(item.lane)}
                  cy={y(at)}
                  r={selected === item.key ? 7 : 4}
                  className={classes.join(' ')}
                  aria-hidden="true"
                />
              );
            }
            return null;
          })}
        </svg>
        <ul className="route-rows" style={{ paddingLeft: gutter }}>
          {items.map((item) => {
            if (item.kind === 'run') {
              const planned = perDay.get(item.country) ?? null;
              const { days, day, total, changed } = figures(item);
              const was = t('plan.runWas', {
                days: t('plan.daysShort', { count: item.days }),
                amount:
                  planned === null
                    ? '—'
                    : t('plan.perDayShortAmount', { amount: formatWhole(planned, lang) }),
              });
              return (
                <li key={item.key} className="route-row" style={{ height: RUN_ROWS * ROW }}>
                  <h3
                    className={[
                      'run-head',
                      item.dear ? 'run-dear' : '',
                      item.stops.every((id) => skipped.has(id)) ? 'run-skipped' : '',
                      changed ? 'run-changed' : '',
                    ].join(' ')}
                    title={changed ? was : undefined}
                  >
                    <span>
                      <Flag country={item.country} />
                      <span className="caps">{countryName(item.country, lang)}</span> ·{' '}
                      {t('plan.daysShort', { count: days })}
                    </span>
                    {day !== null && (
                      // Pressed, every row says what it costs in all, or a day again.
                      <button
                        type="button"
                        className="run-amount"
                        aria-pressed={sums}
                        title={t(sums ? 'plan.showPerDay' : 'plan.showTotals')}
                        onClick={() => {
                          showSums(!sums);
                        }}
                      >
                        {amount(day, total)}
                      </button>
                    )}
                    {changed && <span className="visually-hidden">{was}</span>}
                  </h3>
                </li>
              );
            }
            if (item.kind === 'stop') {
              const { stop } = item;
              // A stop a booked ticket goes to stays where it is.
              const held = heldBy(plan, stop);
              return (
                <li
                  key={item.key}
                  draggable={!locked && held === null}
                  className={[
                    'route-row',
                    'stop-item',
                    dragging === stop.id ? 'stop-dragging' : '',
                    over === stop.id && dragging !== stop.id ? 'stop-over' : '',
                  ].join(' ')}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', stop.id);
                    setDragging(stop.id);
                  }}
                  onDragOver={(event) => {
                    // A drop that would part a booked way is not taken: no place, no message.
                    if (!allowed(ids.indexOf(dragging ?? ''), ids.indexOf(stop.id))) return;
                    event.preventDefault();
                    setOver(stop.id);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const from = ids.indexOf(dragging ?? '');
                    const to = ids.indexOf(stop.id);
                    setDragging(null);
                    setOver(null);
                    if (from >= 0 && from !== to && allowed(from, to)) onMove(from, to);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                >
                  {typing?.stop.id === stop.id ? (
                    daysForm(
                      typing,
                      'stop',
                      <>
                        <StatusIcon status={stop.status} />
                        {stop.expectedPerDay !== null && (
                          <span className="stop-day">
                            {amount(stop.expectedPerDay, stop.cost.expected)}
                          </span>
                        )}
                      </>,
                    )
                  ) : (
                    <button
                      type="button"
                      className={`stop${stop.unresolved ? ' stop-unresolved' : ''}${skipped.has(stop.id) ? ' stop-skipped' : ''}`}
                      aria-pressed={selected === stop.id}
                      title={
                        stop.unresolved
                          ? t('plan.undecidedHint')
                          : stop.transfer
                            ? t('plan.transferHint')
                            : t('plan.daysHint')
                      }
                      aria-keyshortcuts={
                        stop.transfer
                          ? 'Alt+ArrowUp Alt+ArrowDown'
                          : 'Alt+ArrowUp Alt+ArrowDown F2'
                      }
                      onClick={() => {
                        onSelect(stop.id);
                      }}
                      onKeyDown={(event) => {
                        // The keyboard's way to drag: Alt with an arrow moves it one place.
                        if (event.key === 'F2') {
                          event.preventDefault();
                          type(stop);
                          return;
                        }
                        const by = { ArrowUp: -1, ArrowDown: 1 }[event.key];
                        const at = ids.indexOf(stop.id);
                        if (locked || !event.altKey || by === undefined) return;
                        event.preventDefault();
                        if (ids[at + by] !== undefined) onMove(at, at + by);
                      }}
                    >
                      <span className="stop-name">
                        {stop.unresolved ? `? ${stop.city}` : stop.city}
                        {stop.transfer ? (
                          <>
                            {' '}
                            <ChangeMark />
                          </>
                        ) : (
                          <>
                            {' · '}
                            {/* Only the days, pressed twice, are typed in their place. */}
                            <span
                              className="stop-days"
                              onDoubleClick={() => {
                                type(stop);
                              }}
                            >
                              {t('plan.daysShort', { count: stop.days })}
                            </span>
                          </>
                        )}
                        {notes[stop.id] !== undefined && (
                          <span className="stop-note"> · {notes[stop.id]}</span>
                        )}
                      </span>
                      <StatusIcon status={stop.status} />
                      {/* What a day there is expected to cost, as its country's is said. */}
                      {!stop.transfer && stop.expectedPerDay !== null && (
                        <span className="stop-day">
                          {amount(stop.expectedPerDay, stop.cost.expected)}
                        </span>
                      )}
                    </button>
                  )}
                </li>
              );
            }
            if (item.kind === 'gap') {
              const { gap } = item;
              const ways = waysOf(gap);
              return (
                <li
                  key={item.key}
                  className="route-row route-gap"
                  style={{ height: (GAP_ROWS + Math.min(1, ways.length)) * ROW }}
                >
                  {/* Days no stop covers, before the way on leaves: a stop to add, or the one
                      before stays longer; how it goes on, when not decided, with its price. */}
                  <div
                    className="gap-block"
                    role="group"
                    aria-label={t('plan.gap', { count: gap.days })}
                  >
                    <strong>{t('plan.gap', { count: gap.days })}</strong>
                    <span className="quiet gap-where">
                      {dayRange(gap.first, gap.last, lang)} ·{' '}
                      {t('plan.gapBetween', { from: gap.after.city, to: gap.next.city })}
                    </span>
                    {ways.length > 0 && (
                      <span className="gap-ways">
                        {ways.map((way) => (
                          <span key={way.mode} className="gap-way">
                            <ModeIcon mode={way.mode} size={14} />
                            {t(`mode.${way.mode}`)}{' '}
                            <span className="mono">
                              ~{formatRange(way.estimate, lang, true)}
                            </span>
                          </span>
                        ))}
                      </span>
                    )}
                    {!locked && (
                      <span className="gap-actions">
                        <button
                          type="button"
                          className="button button-small"
                          onClick={() => {
                            onAddAfter(gap.after);
                          }}
                        >
                          {t('plan.gapAdd')}
                        </button>
                        {!gap.after.transfer && !daysHeld(plan, gap.after) && (
                          <button
                            type="button"
                            className="button button-small"
                            aria-label={t('plan.gapLonger', { city: gap.after.city })}
                            title={t('plan.gapLonger', { city: gap.after.city })}
                            onClick={() => {
                              onDays(gap.after, gap.after.days + gap.days);
                            }}
                          >
                            {t('plan.gapLongerShort')}
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </li>
              );
            }
            const { variant, stop } = item;
            const shownNow = picked.includes(variant.id);
            return (
              <li key={item.key} className="route-row">
                {typing?.stop.id === stop.id ? (
                  daysForm(
                    typing,
                    'branch-place branch-shown',
                    stop.expectedPerDay && (
                      <span className="mono">
                        {amount(stop.expectedPerDay, stop.cost.expected)}
                      </span>
                    ),
                  )
                ) : (
                  <button
                    type="button"
                    className={shownNow ? 'branch-place branch-shown' : 'branch-place'}
                    aria-pressed={selected === item.key}
                    title={variantName(variant)}
                    aria-keyshortcuts="F2"
                    onClick={() => {
                      onSelect(item.key);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'F2') return;
                      event.preventDefault();
                      startTyping(stop, (days) => {
                        onPlaceDays(variant, item.at, stop, days);
                      });
                    }}
                  >
                    <span>
                      {stop.city} ·{' '}
                      <span
                        className="stop-days"
                        onDoubleClick={() => {
                          startTyping(stop, (days) => {
                            onPlaceDays(variant, item.at, stop, days);
                          });
                        }}
                      >
                        {t('plan.daysShort', { count: stop.days })}
                      </span>
                    </span>{' '}
                    {stop.expectedPerDay && (
                      <span className="mono">
                        {amount(stop.expectedPerDay, stop.cost.expected)}
                      </span>
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {after}
    </section>
  );
}
